import { useState, useEffect } from 'react'
import TodoForm from './components/TodoForm'
import TodoList from './components/TodoList'
import TodoFilter from './components/TodoFilter'
import TodoStats from './components/TodoStats'
import LoginForm from './components/LoginForm'
import Loading from './components/Loading'
import Toast from './components/Toast'
import './App.css'

export interface Todo {
  id: string
  text: string
  completed: boolean
  created_at: string
  sort_order: number
  completed_at: string | null
  completed_by: string | null
}

function App() {
  const [todos, setTodos] = useState<Todo[]>([])
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all')
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState({ show: false, message: '' })
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [username, setUsername] = useState('')

  useEffect(() => {
    // Check if user is already logged in
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/me', {
        credentials: 'include' // Include cookies
      })
      
      if (response.ok) {
        const user = await response.json()
        setIsAuthenticated(true)
        setUsername(user.username)
        fetchTodos()
      } else {
        setIsAuthenticated(false)
        setLoading(false)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      setIsAuthenticated(false)
      setLoading(false)
    }
  }

  const handleLogin = (username: string) => {
    setIsAuthenticated(true)
    setUsername(username)
    fetchTodos()
    showToast(`Welcome, ${username}!`)
  }

  const handleLogout = async () => {
    setLoading(true)
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      })
      
      setIsAuthenticated(false)
      setUsername('')
      setTodos([])
      showToast('Logged out successfully')
    } catch (error) {
      console.error('Logout error:', error)
      showToast('Failed to log out')
    } finally {
      setLoading(false)
    }
  }

  const showToast = (message: string) => {
    setToast({ show: true, message })
    setTimeout(() => setToast({ show: false, message: '' }), 3000)
  }

  const fetchTodos = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/todos', {
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch todos')
      }
      
      const data = await response.json()
      
      // Convert completed from 0/1 to boolean if needed
      const processedTodos = (data.todos || []).map((todo: any) => ({
        ...todo,
        completed: typeof todo.completed === 'number' ? Boolean(todo.completed) : todo.completed,
        sort_order: todo.sort_order || 0 // Default to 0 if not set
      }))
      
      setTodos(processedTodos)
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to load todos')
    } finally {
      setLoading(false)
    }
  }

  const createTodo = async (text: string) => {
    setLoading(true)
    try {
      const response = await fetch('/api/todos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text }),
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Failed to create todo')
      }
      
      const newTodo = await response.json()
      // Ensure completed is boolean and sort_order exists
      newTodo.completed = Boolean(newTodo.completed)
      newTodo.sort_order = newTodo.sort_order || 0
      
      setTodos(prevTodos => [newTodo, ...prevTodos])
      showToast('Todo added successfully')
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to add todo')
    } finally {
      setLoading(false)
    }
  }

  const toggleTodo = async (id: string) => {
    setLoading(true)
    try {
      const response = await fetch(`/api/todos/${id}/toggle`, {
        method: 'PATCH',
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Failed to toggle todo')
      }
      
      const updatedTodo = await response.json()
      // Ensure completed is boolean
      updatedTodo.completed = Boolean(updatedTodo.completed)
      
      setTodos(prevTodos => 
        prevTodos.map(todo => todo.id === id ? updatedTodo : todo)
      )
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to toggle todo')
    } finally {
      setLoading(false)
    }
  }

  const updateTodo = async (id: string, text: string) => {
    setLoading(true)
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text }),
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Failed to update todo')
      }
      
      const updatedTodo = await response.json()
      // Ensure completed is boolean
      updatedTodo.completed = Boolean(updatedTodo.completed)
      
      setTodos(prevTodos => 
        prevTodos.map(todo => todo.id === id ? updatedTodo : todo)
      )
      showToast('Todo updated successfully')
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to update todo')
    } finally {
      setLoading(false)
    }
  }

  const deleteTodo = async (id: string) => {
    setLoading(true)
    try {
      const response = await fetch(`/api/todos/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete todo')
      }
      
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== id))
      showToast('Todo deleted successfully')
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to delete todo')
    } finally {
      setLoading(false)
    }
  }

  const clearCompleted = async () => {
    const completedTodos = todos.filter(todo => todo.completed)
    if (completedTodos.length === 0) return
    
    setLoading(true)
    try {
      // Using Promise.all to delete all completed todos in parallel
      const deletePromises = completedTodos.map(todo => 
        fetch(`/api/todos/${todo.id}`, { 
          method: 'DELETE',
          credentials: 'include'
        })
      )
      
      await Promise.all(deletePromises)
      
      setTodos(prevTodos => prevTodos.filter(todo => !todo.completed))
      showToast('Completed todos cleared')
    } catch (error) {
      console.error('Error:', error)
      showToast('Failed to clear completed todos')
    } finally {
      setLoading(false)
    }
  }

  // Handle todo reordering after drag and drop
  const reorderTodos = async (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex === destinationIndex) return;
    
    // Create a copy of the current todos
    const reorderedTodos = [...todos];
    
    // Get the todo that's being moved
    const [removedTodo] = reorderedTodos.splice(sourceIndex, 1);
    
    // Insert it at the destination
    reorderedTodos.splice(destinationIndex, 0, removedTodo);
    
    // Update the local state immediately for a responsive UI
    setTodos(reorderedTodos);
    
    try {
      // Send the reordered todos to the server
      const orderedIds = reorderedTodos.map(todo => todo.id);
      
      setLoading(true);
      const response = await fetch('/api/todos/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderedIds }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to save todo order');
      }
      
      // Get the updated todos from the server with correct sort_order values
      const data = await response.json();
      
      if (data.todos) {
        // Update with the latest todos from the server
        const processedTodos = data.todos.map((todo: any) => ({
          ...todo,
          completed: typeof todo.completed === 'number' ? Boolean(todo.completed) : todo.completed
        }));
        
        setTodos(processedTodos);
      }
    } catch (error) {
      console.error('Error saving todo order:', error);
      showToast('Failed to save todo order');
      // Revert to the original order if the server update fails
      fetchTodos();
    } finally {
      setLoading(false);
    }
  };

  const filteredTodos = (() => {
    switch (filter) {
      case 'active':
        return todos.filter(todo => !todo.completed)
      case 'completed':
        return todos.filter(todo => todo.completed)
      default:
        return todos
    }
  })()

  const activeTodos = todos.filter(todo => !todo.completed)

  // If still checking auth status, show loading
  if (loading && !isAuthenticated) {
    return <Loading />
  }

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <div className="container">
        <LoginForm onLogin={handleLogin} />
        <Toast show={toast.show} message={toast.message} />
      </div>
    )
  }

  return (
    <div className="container">
      <header>
        <div className="user-header">
          <h1>Lance Casa</h1>
          <div className="user-info">
            <span>{username}</span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
        <TodoForm onSubmit={createTodo} />
      </header>

      <main>
        <TodoFilter 
          currentFilter={filter} 
          onFilterChange={setFilter} 
        />

        <TodoList 
          todos={filteredTodos} 
          onToggle={toggleTodo}
          onUpdate={updateTodo}
          onDelete={deleteTodo}
          filter={filter}
          onReorder={reorderTodos}
        />

        <TodoStats 
          itemsLeft={activeTodos.length} 
          onClearCompleted={clearCompleted} 
        />
      </main>

      {loading && <Loading />}
      <Toast 
        show={toast.show} 
        message={toast.message} 
      />

      <footer>
        <p>Built with Cloudflare Pages, D1, and Hono</p>
        <a href="https://github.com/yourusername/todo-app-cloudflare" 
           target="_blank" 
           className="github-link"
           rel="noreferrer">
          <i className="fab fa-github"></i> View on GitHub
        </a>
      </footer>
    </div>
  )
}

export default App 